import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Crown, Trophy, Medal, Flame } from 'lucide-react';
import { heatColor } from '@/pages/HeatmapPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// P107 (Feb 2026) — Show the top N most-undervalued composites per tier
// on the Dashboard strip. Bumped from 3 → 6 (Feb 2026 v2) per user
// request; the endpoint accepts `?limit=` so the number is a client-
// side call, not baked in.
const TOP_N = 4;

const ROWS = [
  { tier: 'king', label: 'Kings', icon: Crown, path: '/kings-heatmap' },
  { tier: 'aristocrat', label: 'Aristocrats', icon: Trophy, path: '/aristocrats-heatmap' },
  { tier: 'achiever', label: 'Achievers', icon: Medal, path: '/achievers-heatmap' },
];

// P107 — Dashboard strip after Streaks At Risk: top-N most undervalued
// (composite) per tier, chips colored by heatmap gradient, linking into
// the free SEO heatmap pages with ?focus= to auto-open the tile modal.
export const HeatmapTop3Strip = () => {
  const [top3, setTop3] = useState(null);

  useEffect(() => {
    axios.get(`${API}/heatmap/top3?limit=${TOP_N}`)
      .then(({ data }) => setTop3(data.top3))
      .catch(() => setTop3(null));
  }, []);

  if (!top3 || !ROWS.some(r => (top3[r.tier] || []).length > 0)) return null;

  return (
    <div className="mb-3 rounded-xl border border-emerald-500/50 bg-card p-3 sm:p-4" data-testid="heatmap-top3-strip">
      <div className="flex items-center gap-1.5 mb-2">
        <Flame className="w-4 h-4 text-emerald-500" />
        <h3 className="text-[18px] font-bold">Most Undervalued Right Now</h3>
        {/* Dark amber on light, light amber on dark — bold both. */}
        <span
          className="text-[10px] font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wide ml-auto hidden sm:inline"
          data-testid="heatmap-top3-heading-tag"
        >
          Undervaluation Heatmaps
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {ROWS.map(({ tier, label, icon: Icon, path }) => {
          const docs = top3[tier] || [];
          if (!docs.length) return null;
          return (
            <div key={tier} className="flex items-center gap-1.5 min-w-0">
              {/* Mobile: label column is fixed to `w-24` (~96px) so
                  every tier's ticker pills line up in the same X
                  position — the longest label word ("Aristocrats")
                  sets the anchor, Kings/Achievers pad to match.
                  Desktop (≥sm): label reverts to natural width. */}
              <Link
                to={path}
                className="flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground shrink-0 transition-colors w-24 sm:w-auto"
                data-testid={`heatmap-top3-label-${tier}`}
              >
                <Icon className="w-3.5 h-3.5 text-amber-500" />{label}
              </Link>
              {/* Chips wrap on tight widths so tickers never overflow;
                  each chip is a fixed width so a 2-char ticker like KO
                  is the same visual size as a 5-char like CDUAF.
                  Mobile: `w-12` (48px) so 4 chips fit on one line
                  next to the 96px label column at 390px viewport;
                  desktop bumps to `w-14` for more visual breathing
                  room. */}
              <div className="flex items-center gap-1 flex-wrap min-w-0">
                {docs.map(d => (
                  <Link key={d.ticker} to={`${path}?focus=${d.ticker}`}
                    className="w-12 sm:w-14 py-1 rounded-md text-white text-[11px] font-black tracking-tight text-center hover:scale-105 transition-transform"
                    style={{ backgroundColor: heatColor(d.color?.composite) }}
                    data-testid={`heatmap-top3-chip-${d.ticker}`}>
                    {d.ticker}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Explainer — quick "what undervalued means here" + sort note.
          Sorted server-side by composite DESC. */}
      <div className="mt-3 pt-2 border-t border-border/40 text-[11px] leading-snug text-muted-foreground space-y-1" data-testid="heatmap-top3-explainer">
        <p>
          <strong className="text-foreground">Undervalued</strong> = high composite score: structurally cheap on its own 3-yr yield history, technically oversold vs its sector ETF, and dividend covered by free cash flow.
        </p>
        <p>Sorted by composite score, highest first. Educational screen, not a buy signal.</p>
      </div>
    </div>
  );
};
